import { StudioRichTooltip } from '@components/RichTooltip/StudioRichTooltip'
import { StudioRichTooltipPresets } from '@components/RichTooltip/StudioRichTooltipPresets'
import {
  useMediumMinimumSizePreference,
  useProjectState
} from '@hooks/useProject'
import { SnappedAngleDirection, useCamera } from '@services/engine/useCamera'
import IconLeftArrowOrbit from '/public/s2_icon_left_arrow_orbit.svg'
import LockCameraOrientationIcon from '/public/temp-icon-orbit-snap.svg'
import { getCmdModifier } from '@services/engine/utils'
import styles from '@styles/components/OrbitControls.module.scss'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { FC } from 'react'
import { ActionButton, Icon, Theme, Tooltip } from 'ui'
import { useTranslations } from 'use-intl'

export const LeftOrbitButton: FC = () => {
  const size = useMediumMinimumSizePreference()
  const { orbit } = useCamera()
  const modifier = getCmdModifier()
  const flags = useFlags()
  const richTooltipEnabled = flags['base-tf-ui-rich-tooltips']
  const t = useTranslations()
  const colorPreference = useProjectState('colorPreference')

  return (
    <ActionButton
      id="horizontal-orbit-slider-left-button"
      size={size}
      className={styles['navigation-btn-rounded-left']}
      quiet
      onClick={() => {
        orbit(SnappedAngleDirection.next, SnappedAngleDirection.stationary)
        document.getElementById('canvas')?.focus()
      }}
    >
      <div className={styles['icon_container_rotated_0_deg']}>
        <div className={styles['navigation-btn__icon']}>
          <IconLeftArrowOrbit />
        </div>
      </div>
      {richTooltipEnabled ? (
        <Theme color={colorPreference}>
          <StudioRichTooltip
            {...StudioRichTooltipPresets.horizontalOrbitSliderLeft}
            keybind={`studio:richTooltip:orbit:left:keybind:${modifier}`}
          />
        </Theme>
      ) : (
        <Tooltip selfManaged placement="bottom">
          {t('studio:tooltips:turnLeft')} (
          {t(`studio:tooltips:turnLeftKeybind:${modifier}`)})
        </Tooltip>
      )}
    </ActionButton>
  )
}

export const RightOrbitButton: FC = () => {
  const size = useMediumMinimumSizePreference()
  const { orbit } = useCamera()
  const modifier = getCmdModifier()
  const flags = useFlags()
  const richTooltipEnabled = flags['base-tf-ui-rich-tooltips']
  const t = useTranslations()
  const colorPreference = useProjectState('colorPreference')

  return (
    <ActionButton
      id="horizontal-orbit-slider-right-button"
      size={size}
      className={styles['navigation-btn-rounded-right']}
      quiet
      onClick={() => {
        orbit(SnappedAngleDirection.previous, SnappedAngleDirection.stationary)
        document.getElementById('canvas')?.focus()
      }}
    >
      <div className={styles['icon_container_rotated_180_deg']}>
        <div className={styles['navigation-btn__icon']}>
          <IconLeftArrowOrbit />
        </div>
      </div>
      {richTooltipEnabled ? (
        <Theme color={colorPreference}>
          <StudioRichTooltip
            {...StudioRichTooltipPresets.horizontalOrbitSliderRight}
            keybind={`studio:richTooltip:orbit:right:keybind:${modifier}`}
          />
        </Theme>
      ) : (
        <Tooltip selfManaged placement="bottom">
          {t('studio:tooltips:turnRight')} (
          {t(`studio:tooltips:turnRightKeybind:${modifier}`)})
        </Tooltip>
      )}
    </ActionButton>
  )
}

// This is actually the top (up) vertical button in the UI.
export const DownOrbitButton: FC = () => {
  const size = useMediumMinimumSizePreference()
  const { orbit } = useCamera()
  const modifier = getCmdModifier()
  const flags = useFlags()
  const richTooltipEnabled = flags['base-tf-ui-rich-tooltips']
  const t = useTranslations()
  const colorPreference = useProjectState('colorPreference')

  return (
    <ActionButton
      id="vertical-orbit-slider-up-button"
      size={size}
      className={styles['navigation-btn-rounded-up']}
      quiet
      onClick={() => {
        orbit(SnappedAngleDirection.stationary, SnappedAngleDirection.next)
        document.getElementById('canvas')?.focus()
      }}
    >
      <div className={styles['icon_container_rotated_180_deg']}>
        <div className={styles['navigation-btn__icon']}>
          <IconLeftArrowOrbit />
        </div>
      </div>
      {richTooltipEnabled ? (
        <Theme color={colorPreference}>
          <StudioRichTooltip
            {...StudioRichTooltipPresets.verticalOrbitSliderUp}
            keybind={`studio:richTooltip:tumble:up:keybind:${modifier}`}
          />
        </Theme>
      ) : (
        <Tooltip selfManaged placement="right">
          {t('studio:tooltips:tiltUp')} (
          {t(`studio:tooltips:tiltUpKeybind:${modifier}`)})
        </Tooltip>
      )}
    </ActionButton>
  )
}

// And this is the bottom (down) vertical button in the UI.
export const UpOrbitButton: FC = () => {
  const size = useMediumMinimumSizePreference()
  const { orbit } = useCamera()
  const modifier = getCmdModifier()
  const flags = useFlags()
  const richTooltipEnabled = flags['base-tf-ui-rich-tooltips']
  const t = useTranslations()
  const colorPreference = useProjectState('colorPreference')

  return (
    <ActionButton
      id="vertical-orbit-slider-down-button"
      size={size}
      className={styles['navigation-btn-rounded-down']}
      quiet
      onClick={() => {
        orbit(SnappedAngleDirection.stationary, SnappedAngleDirection.previous)
        document.getElementById('canvas')?.focus()
      }}
    >
      <div className={styles['icon_container_rotated_0_deg']}>
        <div className={styles['navigation-btn__icon']}>
          <IconLeftArrowOrbit />
        </div>
      </div>
      {richTooltipEnabled ? (
        <Theme color={colorPreference}>
          <StudioRichTooltip
            {...StudioRichTooltipPresets.verticalOrbitSliderDown}
            keybind={`studio:richTooltip:tumble:down:keybind:${modifier}`}
          />
        </Theme>
      ) : (
        <Tooltip selfManaged placement="right">
          {t('studio:tooltips:tiltDown')} (
          {t(`studio:tooltips:tiltDownKeybind:${modifier}`)})
        </Tooltip>
      )}
    </ActionButton>
  )
}

export const ToggleOrbitConstraintsButton: FC = () => {
  const size = useMediumMinimumSizePreference()
  const { cameraOrbitSnapping } = useCamera()
  const [isCameraOrbitSnappingEnabled, setCameraOrbitSnappingEnabled] =
    cameraOrbitSnapping()
  const cameraOrbitSnappingCurrentlyEnabled =
    isCameraOrbitSnappingEnabled() ?? false
  const flags = useFlags()
  const richTooltipEnabled = flags['base-tf-ui-rich-tooltips']
  const t = useTranslations()
  const colorPreference = useProjectState('colorPreference')

  return (
    <div className={styles['lock-camera-button-container']}>
      <ActionButton
        id="toggle-orbit-constraints-button"
        size={size}
        className={styles['action-btn']}
        toggles
        selected={cameraOrbitSnappingCurrentlyEnabled}
        onClick={() => {
          setCameraOrbitSnappingEnabled(!cameraOrbitSnappingCurrentlyEnabled)
        }}
      >
        <Icon slot="icon">
          <LockCameraOrientationIcon />
        </Icon>
        {!richTooltipEnabled && (
          <Tooltip selfManaged placement="bottom">
            {t('studio:tooltips:snapCameraOrientation')}
          </Tooltip>
        )}
      </ActionButton>
      {richTooltipEnabled && (
        <Theme color={colorPreference}>
          <StudioRichTooltip {...StudioRichTooltipPresets.snapView} />
        </Theme>
      )}
    </div>
  )
}
